package com.havn.app.features.notifications.data

import com.havn.app.core.logging.HavnLogger
import com.havn.app.features.notifications.domain.HavnDeviceToken
import com.havn.app.features.notifications.domain.HavnNotificationPreferences
import com.havn.app.features.notifications.domain.HavnNotificationSettings
import com.havn.app.features.notifications.domain.HavnNotificationType
import com.havn.app.features.notifications.domain.NotificationRepository
import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.postgrest.from
import io.github.jan.supabase.postgrest.postgrest
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put

class SupabaseNotificationRepository(
    private val client: SupabaseClient
) : NotificationRepository {
    override suspend fun preferences(userId: String): Result<HavnNotificationPreferences> = runCatching {
        val rows = client.from(NOTIFICATION_PREFERENCES)
            .select {
                filter { eq("user_id", userId) }
            }
            .decodeList<NotificationPreferenceRow>()
        val settings = client.from(NOTIFICATION_SETTINGS)
            .select {
                filter { eq("user_id", userId) }
                limit(1)
            }
            .decodeSingleOrNull<NotificationSettingsRow>()
            ?.toDomain()
            ?: HavnNotificationSettings(timezone = currentDeviceTimezone())

        HavnNotificationPreferences(
            enabledByType = HavnNotificationType.entries.associateWith { type ->
                rows.firstOrNull { it.type == type.databaseValue }?.enabled ?: true
            },
            settings = settings
        )
    }.onFailure {
        HavnLogger.error("Notifications", "Failed to load preferences for $userId", it)
    }

    override suspend fun registerDeviceToken(token: HavnDeviceToken): Result<Unit> = runCatching {
        client.postgrest.rpc(
            function = "register_device_token",
            parameters = buildJsonObject {
                put("p_token", token.token)
                put("p_platform", token.platform.databaseValue)
                token.deviceName?.let { put("p_device_name", it) }
                token.appVersion?.let { put("p_app_version", it) }
            }
        )
        Unit
    }.onFailure {
        HavnLogger.error("Notifications", "Failed to register device token", it)
    }

    override suspend fun setPreference(type: HavnNotificationType, enabled: Boolean): Result<Unit> = runCatching {
        client.postgrest.rpc(
            function = "set_notification_preference",
            parameters = buildJsonObject {
                put("p_type", type.databaseValue)
                put("p_enabled", enabled)
            }
        )
        Unit
    }.onFailure {
        HavnLogger.error("Notifications", "Failed to update preference ${type.databaseValue}", it)
    }

    override suspend fun setQuietHours(settings: HavnNotificationSettings): Result<Unit> = runCatching {
        client.postgrest.rpc(
            function = "set_notification_quiet_hours",
            parameters = buildJsonObject {
                put("p_enabled", settings.quietHoursEnabled)
                put("p_start", settings.quietHoursStart)
                put("p_end", settings.quietHoursEnd)
                put("p_timezone", settings.timezone.ifBlank { currentDeviceTimezone() })
            }
        )
        Unit
    }.onFailure {
        HavnLogger.error("Notifications", "Failed to update quiet hours", it)
    }

    private companion object {
        const val NOTIFICATION_PREFERENCES = "notification_preferences"
        const val NOTIFICATION_SETTINGS = "notification_settings"
    }
}

expect fun currentDeviceTimezone(): String

@Serializable
private data class NotificationPreferenceRow(
    val type: String,
    val enabled: Boolean = true
)

@Serializable
private data class NotificationSettingsRow(
    @SerialName("quiet_hours_enabled") val quietHoursEnabled: Boolean = false,
    @SerialName("quiet_hours_start") val quietHoursStart: String = "22:00",
    @SerialName("quiet_hours_end") val quietHoursEnd: String = "07:00",
    val timezone: String = "UTC"
) {
    fun toDomain(): HavnNotificationSettings = HavnNotificationSettings(
        quietHoursEnabled = quietHoursEnabled,
        quietHoursStart = quietHoursStart.take(5),
        quietHoursEnd = quietHoursEnd.take(5),
        timezone = timezone
    )
}
